import { NurseLayout } from "@/components/layout/nurse-layout";
import { Link } from "wouter";
import { ArrowLeft, GraduationCap, CheckCircle, ExternalLink, BookOpen } from "lucide-react";

const RESEARCH_ROUTES = [
  {
    title: "MSc in Advanced Practice / Clinical Research",
    level: "Taught MSc",
    duration: "1 year FT / 2–3 years PT",
    colour: "border-amber-300 bg-amber-50",
    titleColor: "text-amber-800",
    desc: "Many UK HEIs (Manchester, King's, UCL, Leeds, Birmingham) offer MSc programmes in Advanced Practice or Clinical Research suitable for specialist nurses. Provides the research methodology and dissertation skills needed for further academic progression.",
    steps: ["Identify HEI with rheumatology or MSK research expertise", "Apply for trust study leave and CPD funding (often via AWT levy)", "Complete dissertation on a rheumatology nursing topic", "Use as a springboard to PhD or clinical academic role"],
  },
  {
    title: "NIHR Clinical Research Network — Nurse Role",
    level: "Research Practice",
    duration: "Ongoing role",
    colour: "border-blue-300 bg-blue-50",
    titleColor: "text-blue-800",
    desc: "The NIHR Clinical Research Network supports nurses to participate in clinical research as Research Nurses or Research Delivery staff. In rheumatology, this means supporting phase 2/3 clinical trials of new biologics and DMARDs.",
    steps: ["Discuss interest in research with your clinical research team", "Apply for Research Nurse or Research Delivery Coordinator posts", "Complete GCP (Good Clinical Practice) training", "Register with NIHR Research Delivery Network"],
  },
  {
    title: "NIHR Academic Clinical Fellowship (ACF) for Nurses",
    level: "Clinical Academic",
    duration: "3 years (75% clinical / 25% research)",
    colour: "border-teal-300 bg-teal-50",
    titleColor: "text-teal-800",
    desc: "NIHR ACFs are competitive fellowships that allow nurses to develop research skills alongside clinical practice. ACF posts in rheumatology are available and typically lead to an NIHR Doctoral Research Fellowship (DRF) application.",
    steps: ["Hold at least Band 6/7 post with some research interest demonstrated", "Apply via NIHR annual ACF competition (check HEE/NIHR website)", "Develop PhD research proposal during fellowship", "Apply for NIHR DRF or MRC fellowship to complete PhD"],
  },
  {
    title: "PhD / Doctorate in Nursing or Health Sciences",
    level: "Doctoral",
    duration: "3–4 years FT / 5–7 years PT",
    colour: "border-purple-300 bg-purple-50",
    titleColor: "text-purple-800",
    desc: "A PhD or professional doctorate (DNP/DClinP) is the standard for Consultant Nurse and clinical academic roles. Research topics in rheumatology nursing include: patient-reported outcomes, shared decision-making, DMARD adherence, nurse-led service evaluation.",
    steps: ["Identify a supervisory team with rheumatology expertise", "Secure funding: NIHR DRF, MRC fellowship, HEE/university studentship", "Publish during PhD: aim for 2–3 journal papers and conference abstracts", "Apply for Consultant Nurse or Lecturer in Nursing post on completion"],
  },
];

const FUNDING_SOURCES = [
  { name: "NIHR Doctoral Research Fellowship (DRF)", url: "https://www.nihr.ac.uk/", desc: "4-year funded fellowship for nurses and AHPs to complete a PhD. Competitive — requires strong supervisory team and preliminary research experience." },
  { name: "Versus Arthritis Research Grants", url: "https://www.versusarthritis.org/", desc: "Grants for arthritis research including nursing studies. Various schemes from early-career to programme grants." },
  { name: "Florence Nightingale Foundation Scholarships", url: "https://florence-nightingale-foundation.org.uk/", desc: "Leadership and research scholarships for nurses and midwives, including travel fellowships for international research visits." },
  { name: "Trust Study Leave & CPD Budgets", url: "#", desc: "Most NHS Trusts allocate a CPD budget per nurse. Band 7/8 staff often have greater access. Negotiate study leave as part of your appraisal PDR." },
];

export default function NurseResearch() {
  return (
    <NurseLayout title="Academic & Research Pathway">
      <Link href="/nurse/career">
        <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Career Hub
        </button>
      </Link>

      <div className="space-y-8">

        <div className="bg-gradient-to-br from-amber-900 to-amber-950 rounded-2xl p-8 text-white">
          <div className="flex items-center gap-4 mb-3">
            <div className="bg-amber-800/60 p-3 rounded-xl">
              <GraduationCap className="h-7 w-7 text-amber-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Academic & Research Pathway</h2>
              <p className="text-amber-300 text-sm">MSc · NIHR · PhD · Clinical Academic Roles</p>
            </div>
          </div>
          <p className="text-amber-200 text-sm leading-relaxed max-w-3xl">
            A research career alongside clinical nursing opens doors to Consultant Nurse and clinical academic appointments. In rheumatology, nurse-led research in areas such as DMARD adherence, treat-to-target, patient-reported outcomes, and shared decision-making is actively valued by BSR, EULAR, and the NIHR.
          </p>
        </div>

        {/* Research routes */}
        <div className="space-y-5">
          {RESEARCH_ROUTES.map((route) => (
            <div key={route.title} className={`rounded-2xl border-2 ${route.colour} p-6`}>
              <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`font-bold text-lg ${route.titleColor}`}>{route.title}</h3>
                    <span className="text-xs bg-white/80 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full">{route.level}</span>
                  </div>
                  <p className="text-xs text-slate-500">{route.duration}</p>
                </div>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed mb-4">{route.desc}</p>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Steps</p>
                <ul className="space-y-1.5">
                  {route.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle className={`h-4 w-4 mt-0.5 shrink-0 ${route.titleColor}`} />
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Funding */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-amber-600" /> Funding Sources
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {FUNDING_SOURCES.map((f) => (
              <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
                className="bg-white rounded-xl border border-slate-200 p-4 hover:border-amber-300 hover:shadow-sm transition-all group block">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-semibold text-sm text-slate-900 group-hover:text-amber-700 transition-colors">{f.name}</p>
                  <ExternalLink className="h-3.5 w-3.5 text-slate-300 group-hover:text-amber-500 shrink-0 mt-0.5 transition-colors" />
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </div>
    </NurseLayout>
  );
}
